import {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import clsx from 'clsx';
import {format, parse} from 'date-fns';
import {getMe} from '@di-shared/api/cci/auth';
import {diApi} from '@di-shared/api/di';
import {RegisterVehicleCommand} from '@di-shared/api/di/__generated';
import {VehicleInspectionRestrictionErrorCode} from '@di-shared/api/di/type';
import {DEFAULT_CACHE_TIME} from '@di-shared/const';
import {emitEventLog} from '@di-shared/features/analytics';
import {Loading} from '@di-shared/features/capital/CapitalTermsDetail';
import Popper from '@di-shared/features/common/Popper';
import useApiError from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import DateInput from '@di-shared/features/public-service/vehicle-inspection/DateInput';
import {ROUTE_PATHS} from '@di-shared/routes';
import {sanitizeLicensePlateNumber} from '@di-shared/utils/vehicle';
import PreventForm from '@shared/PreventForm';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {isValidDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

interface VehicleInspectionReservationProps {
  puid: string;
}

type VehicleInspectionReservationFormFields = Omit<RegisterVehicleCommand, 'puid'>;

function VehicleInspectionNewVehicle(props: VehicleInspectionReservationProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      {/**
       * [2025-09-29:Jeyoung Lee] FIXME:loading 수정
       */}
      <Suspense fallback={<Loading />}>
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

// yyyy-MM-dd → Date 변환
function stringToDate(dateString: string): Date | undefined {
  if (!dateString) {
    return undefined;
  }
  // 안전하게 파싱 (포맷 보장 일 때)
  return parse(dateString, 'yyyy-MM-dd', new Date());
}

// Date → yyyy-MM-dd 변환
function dateToString(date: Date | null | undefined): string | undefined {
  if (date === undefined) {
    return undefined;
  }

  if (!date) {
    return '';
  }
  return format(date, 'yyyy-MM-dd');
}

function Inner({puid}: VehicleInspectionReservationProps) {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const {openAlert} = useCcWebviewUtilsHelper();
  const {resolveError} = useApiError();
  const {data} = useSuspenseQuery({
    queryKey: [getQueryKey(getMe)],
    queryFn: () => getMe(),
    ...DEFAULT_CACHE_TIME,
  });

  /**
   * [2025-09-25:Jeyoung Lee]
   * form에서는 날짜를 yyyy-MM-dd로 관리
   * DateInput과 통신할 때는 Date 객체로 통신
   */
  const [form, setForm] = useState<VehicleInspectionReservationFormFields>({
    birthday: data.birthdate ? `${data.birthdate.slice(0, 4)}-${data.birthdate.slice(4, 6)}-${data.birthdate.slice(6, 8)}` : '',
    licensePlateNumber: '',
  });
  const [isTouchSubmit, setIsTouchSubmit] = useState(false);
  const [isLicensePlateNumberError, setIsLicensePlateNumberError] = useState(false);

  const isBirthdayValid = isValidDate(form.birthday);
  const isBirthdayError = isTouchSubmit && !isBirthdayValid;

  const isRegistrationDateValid = form.registrationDate === undefined || isValidDate(form.registrationDate);
  const isRegistrationDateError = isTouchSubmit && !isRegistrationDateValid;

  const isLastInspectionDateValid = form.lastInspectionDate === undefined || isValidDate(form.lastInspectionDate);
  const isLastInspectionDateError = isTouchSubmit && !isLastInspectionDateValid;

  const isFormValid = isBirthdayValid && isRegistrationDateValid && isLastInspectionDateValid;

  function updateFormField(update: Partial<VehicleInspectionReservationFormFields>) {
    const licensePlateNumber = sanitizeLicensePlateNumber(form.licensePlateNumber, update.licensePlateNumber);

    setForm((prev) => ({...prev, ...update, licensePlateNumber}));
  }

  const [submitCarInspectionReservationForm] = usePendingHandler(
    async () => {
      setIsLicensePlateNumberError(false);

      const requestBody = {
        puid,
        ...form,
      };
      // POST /public-service/inspection/register
      await diApi.publicServiceInspection.registerVehicle({
        requestBody: {
          ...requestBody,
          licensePlateNumber: requestBody.licensePlateNumber.trim(),
        },
      });
      await openAlert({
        title: '',
        message: t('T30493'),
        okText: t('T30494'),
      });
      backOrClose();
    },
    async (error: any) => {
      const resultCode = error.resultCode;
      const authFailCount = error.data?.authFailCount;
      /**
       * [2025-09-22:Jeyoung Lee] api 관련
       * @link https://hmg.dooray.com/task/3524050935366243006/4119084770680354886
       */
      if (resultCode === VehicleInspectionRestrictionErrorCode.AUTHENTICATION_FAILED) {
        await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(
          ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_OWNER_AUTHENTICATION_FAILURE.getFullPath(),
          {
            birthDay: form.birthday,
            licensePlateNumber: form.licensePlateNumber,
            authFailCount,
          }
        );
        backOrClose();
        return;
      }
      if (
        resultCode === VehicleInspectionRestrictionErrorCode.ALREADY_REGISTERED ||
        resultCode === VehicleInspectionRestrictionErrorCode.MAXIMUM_REGISTERED
      ) {
        await ccWebviewUtils.openBottomSheetWebViewUrl<boolean>(ROUTE_PATHS.BOTTOM_SHEET_PUBLIC_SERVICE_GENERAL_FAILURE.getFullPath(), {
          birthDay: form.birthday,
          licensePlateNumber: form.licensePlateNumber,
          authFailCount,
        });
        return;
      }
      if (error?.code === 4000) {
        setIsLicensePlateNumberError(true);
        return;
      }
      const [openErrorAlert] = await resolveError(error);
      openErrorAlert();
    }
  );

  return (
    <>
      <main className="content">
        <PreventForm onSubmit={() => {}}>
          <section className="section">
            <div className="section_heading">
              <h2 className="section_heading_title">{t('T30495')}</h2>
            </div>
            <div className="service_info_area date_area">
              <h3 className="info_title">{`${t('T30496')} (${t('T30308')})`}</h3>
              <DateInput
                initialDate={stringToDate(form.birthday)}
                onChange={(date) => updateFormField({birthday: dateToString(date)})}
                isError={isBirthdayError}
              />
              {isBirthdayError && <p className="txt_error body_small_r">{t('T30500')}</p>}
            </div>
            <div className="service_info_area">
              <h3 className="info_title">{`${t('T30501')} (${t('T30308')})`}</h3>
              <div className="input_info year">
                <label
                  htmlFor="vehicleRegistrationNumber"
                  className="blind"
                >
                  {t('T30501')}
                </label>
                <LicensePlateNumberInput
                  licensePlateNumber={form.licensePlateNumber}
                  isError={isLicensePlateNumberError}
                  onChange={(licensePlateNumber) => updateFormField({licensePlateNumber})}
                />
              </div>
              {isLicensePlateNumberError && <p className="txt_error body_small_r">{t('T30502')}</p>}
            </div>

            <div className="section_heading type_sub">
              <h3 className="section_heading_title">{t('T30503')}</h3>
              <p className="section_heading_desc">{t('T30504')}</p>
            </div>
            <div className="service_info_area date_area">
              <h3 className="info_title">
                {`${t('T30505')} (${t('T30309')})`}
                <RegistrationDateTooltip />
              </h3>
              <DateInput
                onChange={(date) => updateFormField({registrationDate: dateToString(date)})}
                isError={isRegistrationDateError}
              />
              {isRegistrationDateError && <p className="txt_error body_small_r">{t('T30506')}</p>}
            </div>
            <div className="service_info_area date_area">
              <h3 className="info_title">
                {`${t('T30507')} (${t('T30309')})`}
                <LastInspectionDateTooltip />
              </h3>
              <DateInput
                onChange={(date) => updateFormField({lastInspectionDate: dateToString(date)})}
                isError={isLastInspectionDateError}
              />
              {isLastInspectionDateError && <p className="txt_error body_small_r">{t('T30508')}</p>}
            </div>
          </section>
        </PreventForm>
      </main>
      {/**
       * [2025-09-19:Jeyoung Lee]
       * main과 submit 버튼의 위상이 같아서 form 안으로 넣을 수가 없음
       * PreventForm 을 root로 빼는 순간 스타일 깨짐
       * portal로도 해결 불가능
       */}
      <div className="floating_action_area">
        <button
          onClick={() => {
            emitEventLog({
              event: 'public_service_interaction',
              data_action: 'vehicle inspection click',
              data_name: '서비스 연동하기',
            });
            if (!isFormValid) {
              setIsTouchSubmit(true);
              return;
            }
            submitCarInspectionReservationForm();
          }}
          className="btn btn_large btn_filled btn_full"
          form="CarInspectionReservationForm"
        >
          {t('T30509')}
        </button>
      </div>
    </>
  );
}

export default VehicleInspectionNewVehicle;

function RegistrationDateTooltip() {
  const {t} = useInferableTranslation();

  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={1.5}
      tooltip={
        <div
          tabIndex={0}
          role="tooltip"
        >
          <strong className="tooltip_title">{t('T30510')}</strong>
          <ul className="tooltip_list">
            <li className="tooltip_item">{t('T30511')}</li>
            <li className="tooltip_item">{t('T30512')}</li>
          </ul>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          type="button"
          className="btn_tooltip"
          aria-controls="tooltipStatement01"
          aria-expanded="false"
          aria-describedby="tooltipBox01"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          ></span>
          <span className="blind">{t('T10044')}</span>
        </button>
      </div>
    </Popper>
  );
}

function LastInspectionDateTooltip() {
  const {t} = useInferableTranslation();

  return (
    <Popper
      placement="bottom"
      className="tooltip"
      marginLeft={1.5}
      tooltip={
        <div
          tabIndex={0}
          role="tooltip"
        >
          <strong className="tooltip_title">{t('T30513')}</strong>
          <ul className="tooltip_list">
            <li className="tooltip_item">{t('T30514')}</li>
            <li className="tooltip_item">{t('T30515')}</li>
          </ul>
        </div>
      }
    >
      <div className="tooltip_area tooltip_area_single">
        <button
          type="button"
          className="btn_tooltip"
          aria-controls="tooltipStatement01"
          aria-expanded="false"
          aria-describedby="tooltipBox02"
        >
          <span
            className="ic ic_information_16px"
            aria-hidden="true"
          ></span>
          <span className="blind">{t('T10044')}</span>
        </button>
      </div>
    </Popper>
  );
}

interface LicensePlateNumberInputProps {
  licensePlateNumber?: string;
  isError: boolean;

  onChange(date: string): void;
}

function LicensePlateNumberInput({licensePlateNumber, isError, onChange}: LicensePlateNumberInputProps) {
  const {t} = useInferableTranslation();

  return (
    <div className={clsx('input_box input_box_field', {input_error: isError})}>
      <input
        id="vehicleRegistrationNumber"
        className="input_text body_large_r"
        inputMode="text"
        placeholder={t('T30526')}
        type="text"
        aria-invalid={isError}
        value={licensePlateNumber}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </div>
  );
}
